import { Grid } from "@mui/material";
import { useMemo } from "react";
import Helpers from "../../../commons/helpers";
import { ICodename, IResource, IRole, RoleResourcePermission } from "../../../commons/interfaces";
import { IRoleBitMask, RoleHelpers } from "../../../commons/roleHelpers";
import { Mode, RoleType } from "../../../constants/enums";
import { IRoleError } from "../../../hooks/role/edit";
import useStrings from "../../../hooks/useStrings";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import Autocomplete from "../../Autocomplete";
import Box from "../../Box";
import CustomStatusCell from "../../CustomStatusCell";
import FormField from "../../FormField";
import Typography from "../../Typography";

export const RoleBasicInfo = ({
    mode,
    data,
    roleGroup,
    roleTypes,
    error,
    services,
    onChangeValue,
}: {
    mode: Mode;
    data: IRole;
    error: IRoleError;
    services: ICodename[];
    roleGroup: ICodename[];
    roleTypes: ICodename[];
    resources: IResource[];
    onChangeValue: (key: keyof IRole) => (value: string | number | RoleResourcePermission[]) => void;
}) => {
    const Strings = useStrings();
    const { userInfo } = useCommonComponentContext();

    const { roleSet, roleDetailType } = useMemo<IRoleBitMask>(
        () => RoleHelpers.getRole(userInfo?.userProfile?.roleType),
        [userInfo?.userProfile?.roleType]
    );

    const { roleSet: fBit, roleDetailType: sBit } = useMemo<IRoleBitMask>(() => RoleHelpers.getRole(data?.type), [data?.type]);

    const disabledSelectRole = useMemo(() => !Helpers.isNullOrEmpty(data?.id) || mode !== Mode.Create, [data?.id]);

    const disabledRoleValues = useMemo(() => {
        const roles = [RoleType.Owner, RoleType.Admin, RoleType.GroupAdmin, RoleType.Member, RoleType.Custom, RoleType.Default];
        const index = roles.findIndex((el) => el === roleDetailType);
        if (roleSet > fBit) return [];
        return roles.splice(0, index + 1).map((el) => String(el));
    }, [roleDetailType, roleSet, fBit]);

  
    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
                <Box alignItems="center" sx={{ display: "flex", flexWrap: "wrap" }}>
                    <Typography variant="h5">{Strings.Role.BASIC_INFO}</Typography>
                    {!Helpers.isNullOrEmpty(data?.id) && !Helpers.isNullOrEmpty(data?.status) && (
                        <Box ml={2}>
                            <CustomStatusCell status={data?.status} />
                        </Box>
                    )}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1}>
                    {RoleHelpers.isServiceSet(userInfo?.userProfile?.roleType) &&
                        roleGroup.map((role) => {
                            const isActive = fBit === role.code;
                            return (
                                <Box
                                    onClick={(e) => {
                                        if (disabledSelectRole) return;
                                        e.stopPropagation();
                                        onChangeValue("type")((role.code as RoleType) | sBit);
                                    }}
                                    sx={({ palette: { info }, functions: { rgba } }) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        px: 2,
                                        py: 0.5,
                                        border: "1px solid #c3c3c3",
                                        borderRadius: 2,

                                        ...(isActive && {
                                            borderColor: info.main,
                                            backgroundColor: rgba(info.main, 0.2),
                                        }),
                                        ...(!disabledSelectRole &&
                                            !isActive && {
                                                "&:hover": {
                                                    cursor: "pointer",
                                                },
                                            }),
                                        ...(disabledSelectRole && {
                                            filter: "grayscale(1)",
                                        }),
                                    })}
                                >
                                    <Typography variant="caption" sx={{}}>
                                        {role.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Autocomplete
                    mode={mode}
                    isSelectedBox
                    disabled={!Helpers.isNullOrEmpty(data?.id)}
                    data={roleTypes || []}
                    label={Strings.Role.TYPE}
                    placeholder={Strings.Role.SELECT_TYPE}
                    errorMessage={error?.type}
                    defaultValue={sBit}
                    onChange={(value) => onChangeValue("type")(fBit | value)}
                    optionDisabled={disabledRoleValues}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <FormField
                    required
                    mode={mode}
                    maxLength={255}
                    label={Strings.Role.NAME_ROLE}
                    defaultValue={data?.roleName}
                    errorMessage={error?.roleName}
                    placeholder={Strings.Role.ENTER_NAME_ROLE}
                    onBlur={onChangeValue("roleName")}
                />
            </Grid>
            {RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType) && !RoleHelpers.isOrganizationUser(userInfo?.userProfile) && (
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        isSelectedBox
                        mode={mode}
                        data={services || []}
                        label={Strings.Role.SERVICE_NAME}
                        placeholder={Strings.Role.SELECT_SERVICE}
                        defaultValue={data?.serviceCode}
                        errorMessage={error?.serviceCode}
                        disabled={!Helpers.isNullOrEmpty(data?.id)}
                        onChange={onChangeValue("serviceCode")}
                    />
                </Grid>
            )}
            <Grid item xs={12} md={RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType) ? 6 : 12}>
                <FormField
                    multiline
                    isFullRow
                    mode={mode}
                    maxLength={255}
                    label={Strings.Role.DESCRIPTION}
                    defaultValue={data?.description}
                    errorMessage={error?.description}
                    placeholder={Strings.Role.ENTER_DESCRIPTION}
                    onBlur={onChangeValue("description")}
                />
            </Grid>
        </Grid>
    );
};

export default RoleBasicInfo;
